import * as Phaser from 'phaser';
import { createLogger } from '../utils/Logger';
import type { MobileInputSnapshot } from '../systems/MobileInputController';

const BASE_RADIUS = 64;
const KNOB_RADIUS = 24;
const AIM_RADIUS = 52;
const PADDING_X = 118;
const PADDING_BOTTOM = 112;

export class MobileControlsOverlay {
  private readonly scene: Phaser.Scene;
  private readonly logger = createLogger('MobileControlsOverlay');
  private readonly movementBase: Phaser.GameObjects.Arc;
  private readonly movementKnob: Phaser.GameObjects.Arc;
  private readonly aimBase: Phaser.GameObjects.Arc;
  private readonly aimKnob: Phaser.GameObjects.Arc;
  private visible = true;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.movementBase = this.createCircle(BASE_RADIUS, 0xb9e77a, 0.14, 0xb9e77a, 0.45);
    this.movementKnob = this.createCircle(KNOB_RADIUS, 0xb9e77a, 0.32, 0xffffff, 0.7);
    this.aimBase = this.createCircle(AIM_RADIUS, 0xecc778, 0.12, 0xecc778, 0.42);
    this.aimKnob = this.createCircle(KNOB_RADIUS * 0.8, 0xecc778, 0.3, 0xffffff, 0.7);
    this.setVisible(false, 'constructor');
    this.resize();
  }

  public update(snapshot: MobileInputSnapshot): void {
    const shouldShow = snapshot.touchCapable && snapshot.enabled;
    this.setVisible(shouldShow, 'update');
    if (!shouldShow) return;

    this.positionPair(
      this.movementBase,
      this.movementKnob,
      snapshot.movementOrigin ?? { x: PADDING_X, y: this.scene.scale.height - PADDING_BOTTOM },
      snapshot.movementPosition,
      BASE_RADIUS
    );
    this.positionPair(
      this.aimBase,
      this.aimKnob,
      snapshot.aimOrigin ?? { x: this.scene.scale.width - PADDING_X, y: this.scene.scale.height - PADDING_BOTTOM },
      snapshot.aimPosition,
      AIM_RADIUS
    );
  }

  public resize(): void {
    this.positionPair(
      this.movementBase,
      this.movementKnob,
      { x: PADDING_X, y: this.scene.scale.height - PADDING_BOTTOM },
      null,
      BASE_RADIUS
    );
    this.positionPair(
      this.aimBase,
      this.aimKnob,
      { x: this.scene.scale.width - PADDING_X, y: this.scene.scale.height - PADDING_BOTTOM },
      null,
      AIM_RADIUS
    );
    this.logger.debug('resize:positioned', {
      width: this.scene.scale.width,
      height: this.scene.scale.height
    });
  }

  public destroy(): void {
    this.movementBase.destroy();
    this.movementKnob.destroy();
    this.aimBase.destroy();
    this.aimKnob.destroy();
    this.logger.debug('destroy:complete');
  }

  private createCircle(
    radius: number,
    fillColor: number,
    fillAlpha: number,
    strokeColor: number,
    strokeAlpha: number
  ): Phaser.GameObjects.Arc {
    return this.scene.add.circle(0, 0, radius, fillColor, fillAlpha)
      .setScrollFactor(0)
      .setDepth(150)
      .setStrokeStyle(2, strokeColor, strokeAlpha);
  }

  private positionPair(
    base: Phaser.GameObjects.Arc,
    knob: Phaser.GameObjects.Arc,
    origin: { x: number; y: number },
    position: { x: number; y: number } | null,
    radius: number
  ): void {
    base.setPosition(origin.x, origin.y);
    const knobPosition = clampPosition(origin, position ?? origin, radius);
    knob.setPosition(knobPosition.x, knobPosition.y);
  }

  private setVisible(isVisible: boolean, source: string): void {
    if (this.visible === isVisible) return;
    this.visible = isVisible;
    [this.movementBase, this.movementKnob, this.aimBase, this.aimKnob].forEach(object => object.setVisible(isVisible));
    this.logger.debug('visibility:changed', { isVisible, source });
  }
}

function clampPosition(
  origin: { x: number; y: number },
  position: { x: number; y: number },
  radius: number
): { x: number; y: number } {
  const dx = position.x - origin.x;
  const dy = position.y - origin.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= radius || distance === 0) {
    return position;
  }

  return {
    x: origin.x + (dx / distance) * radius,
    y: origin.y + (dy / distance) * radius
  };
}
